/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPut } from 'FetchAPIs';

const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorLoadingDeviceForLocation: 'Error loading devices for location. {message}',
  errorLoadingParentLocationsForLocation: 'Error loading parent location for location. {message}',
  errorLoadingAssetsForLocation: 'Error loading assets for location. {message}',
  errorLoadingSystemsForSiteLocations: 'Error loading systems. {message}',
  errorRemovingDevicesFromSiteLocations: 'Error removing devices from location. {message}',
  errorLoadingAssetForSiteLocation: 'Error loading assets for location. {message}',
});

export const getAssetsForLocationAPI = async (siteId: string, locationId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/locations/${locationId}/assets`, null, null)
    .then(res => res)
    .catch(error => {
      throw new I18NError(messages.errorLoadingAssetsForLocation, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads devices for a specific site and location API
 * @param siteId -  site id
 * @param locationId - location id
 * @returns existing devices for the specified site and location
 */
export const getDevicesForLocation = async (
  siteId: string,
  locationId: string,
  page = 0,
  limit = 1000
): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/locations/${locationId}/devices`, null, {
    page,
    limit,
  })
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDeviceForLocation, {
        message: formatErrorResponse(error),
      });
    });

export const getParentLocationForLocation = async (
  siteId: string,
  systemId: string,
  locationId: string
): Promise<any> =>
  fetchGet(
    `${baseHierarchiesURL}/sites/${siteId}/systems/${systemId}/locations/${locationId}/parentlocations`,
    null,
    null
  )
    .then(res => res)
    .catch(error => {
      throw new I18NError(messages.errorLoadingParentLocationsForLocation, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads systems that belongs to a specific site and location API
 * @param siteId -  site id
 * @param locationId - location id
 * @returns systems that the locations exists at for the specified site and location
 */
export const getSystemsForSiteLocation = async (siteId: string, locationId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/locations/${locationId}/systems`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSystemsForSiteLocations, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that removes devices from a location API
 * @param siteId -  site id
 * @param locationId - location id
 * @param deviceDDIds - array of deviceDDIds to be removed from the location
 */
export const removeDevicesFromSiteLocation = async (
  siteId: string,
  locationId: string,
  deviceDDIds: string[],
  userEmailId: string
): Promise<any> =>
  fetchPut(
    `${baseHierarchiesURL}/sites/${siteId}/locations/${locationId}/relations/devices/remove`,
    deviceDDIds,
    { userEmailId },
    undefined
  ).catch(error => {
    throw new I18NError(messages.errorRemovingDevicesFromSiteLocations, {
      message: formatErrorResponse(error),
    });
  });

export const getAssetsForSiteLocation = async (
  siteId: string,
  locationId: string,
  page = 0,
  limit = 1000
): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/locations/${locationId}/assets`, null, {
    page,
    limit,
  })
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingAssetForSiteLocation, {
        message: formatErrorResponse(error),
      });
    });
